import { useEffect, useRef, useState } from "react";
import styles from "./FitGuideButton.scss";
import classnamesBind from "classnames/bind";
import getWindow from "../../utils/window";
import { useFeaturesReady } from "../../context/featuresReady/FeaturesReadyContext";
import { getFeature } from "../../utils/features";
import { fireFitGuideLinkClickAnalytics } from "../../analytics/pageInteraction";
import { FitGuideButtonProps } from "../../types/components/fitGuideButton";

const classnames = classnamesBind.bind(styles);

type Window = {
  asos: {
    eventBus: {
      emit: (event: string, data: Record<string, unknown>) => void;
    };
  };
};

export default function FitGuideButton({
  fitTypeId,
  productTypeId,
  brandId,
  browseLanguage,
  browseCountry,
  storeCode,
  keyStoreDataVersion,
  gender,
  fitGuideUrl,
  getTranslation,
}: FitGuideButtonProps) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [showFitGuide, setShowFitGuide] = useState(false);
  const { areFeaturesReady } = useFeaturesReady() as {
    areFeaturesReady: boolean;
  };

  useEffect(() => {
    if (areFeaturesReady) {
      setShowFitGuide(
        getFeature("pdp-visual-fit-guide").isEnabled() &&
          (
            getFeature("pdp-visual-fit-guide").getVariables() as unknown as {
              bucket?: string;
            }
          )?.bucket === "on"
      );
    }
  }, [areFeaturesReady]);

  if (!fitTypeId || !showFitGuide) {
    return null;
  }

  const buildFitGuideUrl = (): string => {
    const url = new URL(fitGuideUrl);
    url.searchParams.append("fitTypeId", fitTypeId.toString());
    url.searchParams.append("productTypeId", productTypeId.toString());
    url.searchParams.append("brandId", brandId.toString());

    if (browseLanguage) {
      url.searchParams.append("browseLanguage", browseLanguage);
    }

    if (browseCountry) {
      url.searchParams.append("browseCountry", browseCountry);
    }

    url.searchParams.append("store", storeCode);
    url.searchParams.append("platform", "web");

    if (keyStoreDataVersion) {
      url.searchParams.append("keyStoreDataVersion", keyStoreDataVersion);
    }
    url.searchParams.append("gender", gender);
    const params = new URLSearchParams(window.location.search);
    const xSiteOrigin = params.get("x-site-origin");

    if (xSiteOrigin) {
      url.searchParams.append("x-site-origin", xSiteOrigin);
    }

    return url.toString();
  };

  const handleClick = () => {
    fireFitGuideLinkClickAnalytics();
    const url = buildFitGuideUrl();
    (getWindow() as unknown as Window).asos.eventBus.emit("fitGuideOpen", {
      url: url,
      focusOnClose: buttonRef.current,
    });
  };

  return (
    <>
      {". "}
      <button
        className={classnames(styles.fitGuideButton)}
        data-testid="fit-guide-button"
        ref={buttonRef}
        onClick={handleClick}
      >
        {getTranslation(
          "fit_guide_link_no_fit_type",
          undefined,
          browseLanguage
        )}
      </button>
    </>
  );
}
